import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";

import {
  ApiError,
  completeAssignment,
  fetchDue,
  postCompletion,
  uncompleteAssignment,
  undoCadenceCompletion,
} from "../lib/api";
import {
  cancelQueuedMutation,
  enqueueMutation,
  replayQueue,
  type QueueMutationKind,
} from "../offline/queue";
import type { DueResponse, DueTask } from "../types";
import { useOnlineStatus } from "./useOnlineStatus";
import { SETTLE_MS, clearAllSettling, clearSettling, markSettling } from "./useSettlingTasks";

export type GroupKey = "due_today" | "overdue" | "whenever" | "done_today";

interface UnsyncedEntry {
  kind: QueueMutationKind;
  idempotencyKey: string;
}

/** UX review (done-feedback) — a save that permanently failed (a non-
 * network, non-401 error), rendered by `Snackbar` as a persistent
 * `role="alert"` row naming the item, with a retry action. Never
 * auto-dismissed. */
export interface SnackbarAlert {
  key: string;
  message: string;
  actionLabel: string;
  onAction: () => void;
  onDismiss?: () => void;
}

export interface SnackbarState {
  message: string;
  onUndo?: () => void;
  /** Sprint-009 B4 — overrides the action button's label (default "Undo").
   * Added for the calendar's undo-409 case (design-phase-b.md §2.9): "That
   * task was already done — undo isn't possible. Open it to see what
   * happened," where the action routes to the task's current location
   * rather than reversing anything. Optional and backward-compatible —
   * every existing `showSnackbar` call in this file omits it and keeps
   * rendering the literal "Undo" label unchanged. */
  actionLabel?: string;
  /** UX review (done-feedback) — persistent "Couldn't save" alerts shown
   * above the regular toast. Optional; every other `Snackbar` caller in
   * the app omits it. When only alerts are showing, `message` is "". */
  alerts?: SnackbarAlert[];
}

/** How long the "Marked X done" toast (and its Undo) stays up. Lengthened
 * from 5s: a farm hand outdoors, in gloves, needs time to notice it. */
export const SNACKBAR_MS = 8000;

/** `Couldn't save "Scrub horse waterers"` — the permanent-failure copy. */
export function saveFailedMessage(title: string): string {
  return `Couldn't save "${title}"`;
}

function removeFromGroup(data: DueResponse, group: GroupKey, taskId: string): DueTask | null {
  const list = data[group];
  const idx = list.findIndex((t) => t.id === taskId);
  if (idx === -1) return null;
  const removed = list[idx] as DueTask;
  data[group] = [...list.slice(0, idx), ...list.slice(idx + 1)];
  return removed;
}

/** Optimistically nudge a flexible chore's local `done_count` (never its
 * server-rendered `phrase` — see `logFlexibleTask`'s docstring). */
function bumpFlexible(data: DueResponse, cadenceId: string, delta: number): DueResponse {
  return {
    ...data,
    flexible_progress: data.flexible_progress.map((f) =>
      f.cadence_id === cadenceId ? { ...f, done_count: Math.max(0, f.done_count + delta) } : f,
    ),
  };
}

function nowIso(): string {
  return new Date().toISOString();
}

/**
 * QA gate sprint-013 Issue #1 (Critical): a cadence-kind task's
 * `assignee_id` is `null` for an unassigned "Up for grabs" occurrence
 * (`app/engine/due.py::scope_due_result` keeps those visible to everyone
 * rather than hiding them). Tapping done on one of those is always done
 * by the signed-in person doing the tapping — sending `assignee_id ?? ""`
 * (the pre-existing bug) hands the required `completed_by: uuid.UUID`
 * field an empty string, which 422s server-side and silently drops the
 * completion behind the optimistic UI. `meId` is the correct fallback:
 * for an ASSIGNED task in this "mine"-scoped list, `assignee_id` already
 * equals the signed-in user's own id (`scope_due_result`'s `visible_ids`
 * is always the single-caller `{actor.user_id}` set), so this is a no-op
 * for that path — it only changes behavior for the previously-broken
 * unassigned one.
 */
function completedByFor(assigneeId: string | null, meId: string): string {
  return assigneeId ?? meId;
}

/** A 401 mid-session (the link/session needs a refresh) — treated the
 * same as a network failure for queueing purposes (the tap is never
 * lost), but surfaced with the distinct reauth banner rather than the
 * offline one. See offline/queue.ts's identical `requiresReauth` check
 * for the replay path. */
function isAuthError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

/** Network error or 401 — the tap gets queued, never dropped. Anything
 * else is a permanent failure the person must be told about. */
function isQueueable(err: unknown): boolean {
  return (err instanceof ApiError && err.isNetworkError) || isAuthError(err);
}

/** What happened to one completion attempt. `failed` = permanent
 * (non-network, non-401) — rolled back and surfaced as an alert. */
type SendOutcome = "ok" | "queued" | "failed";

/** A retry re-sends the SAME logical mutation: same idempotency key, same
 * tap-time `completed_at`, so it can never double-write server state. */
interface Attempt {
  idempotencyKey: string;
  completedAt: string;
}

/**
 * `meId`: the signed-in user's own id (`useMe()`'s `state.id`, threaded in
 * from `app.tsx`). Needed so an UNASSIGNED ("Up for grabs") cadence-kind
 * task's `completed_by` has somewhere correct to fall back to — see
 * `completedByFor` below.
 */
export function useDue(meId: string) {
  const [data, setData] = useState<DueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<SnackbarState | null>(null);
  const [failures, setFailures] = useState<SnackbarAlert[]>([]);
  // Sprint-003 A5: set when the offline queue's replay stops on a 401 —
  // "your link needs a refresh" (see offline/queue.ts's `requiresReauth`).
  // Distinct from `isOffline`: this is a real, reachable server actively
  // rejecting the stale session, not a connectivity problem.
  const [needsReauth, setNeedsReauth] = useState(false);
  const { isOffline, reportNetworkFailure, reportNetworkSuccess } = useOnlineStatus();

  // Where a done-today task originally came from, so Undo/tap-again can
  // return it to the right section. Cleared once the row is confirmed
  // permanently done (or reopened).
  const originRef = useRef<Map<string, GroupKey>>(new Map());
  // The completions-row id for a completed cadence occurrence — undoing a
  // cadence-kind row is DELETE /api/v1/completions/{id}, keyed by this id
  // (see api.ts's undoCadenceCompletion).
  const completionIdRef = useRef<Map<string, string>>(new Map());
  // Mutations queued locally but not yet confirmed sent to the server —
  // if Undo happens before these sync, we simply cancel the queued
  // mutation instead of round-tripping a doomed reversal call.
  const unsyncedRef = useRef<Map<string, UnsyncedEntry>>(new Map());
  const snackbarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Which item the current toast is about, so a permanent failure can pull
  // a "Marked X done" toast that has just become untrue.
  const toastKeyRef = useRef<string | null>(null);
  // Per-task settle timers: the row stays in place (checked) for
  // `SETTLE_MS` before it moves to Done today. Each row has its own timer,
  // so rapid taps on several rows never interfere with each other.
  const settleTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // The in-flight completion send per item, so an Undo tapped before the
  // POST resolves waits for it (and then cancels the queued copy or
  // reverses the real one) instead of racing it.
  const inflightRef = useRef<Map<string, Promise<SendOutcome>>>(new Map());
  // The live completion attempt per task. Undo clears it, so a permanent
  // failure landing AFTER the person already un-did the task doesn't
  // resurrect an alert for something they no longer want done.
  const attemptRef = useRef<Map<string, string>>(new Map());
  // The pending Undo per task. A completion tapped while an Undo is still
  // waiting on the previous POST is sent only AFTER that Undo resolves, so
  // the server sees complete -> uncomplete -> complete in tap order. (An
  // assignment's status write is last-writer-wins; without this, a fast
  // tap/undo/tap on a slow link can end with the server holding "open"
  // while the screen shows done.)
  const undoInflightRef = useRef<Map<string, Promise<void>>>(new Map());

  const refresh = useCallback(async (date?: string) => {
    setError(null);
    try {
      // Sprint-003 A5: Today/My list are always the signed-in person's OWN
      // personal list — `scope=mine` (the server default too, but explicit
      // here matches design-spec-today-card.md's "personal Today" contract
      // rather than relying on an implicit default).
      const response = await fetchDue(date, "mine");
      setData(response);
      setNeedsReauth(false);
    } catch (err) {
      if (isAuthError(err)) {
        // Session died mid-visit — keep whatever data is already on
        // screen (never blank it out from under the person) and surface
        // the gentle reauth banner instead of the generic error copy.
        setNeedsReauth(true);
      } else {
        setError(err instanceof Error ? err.message : "Couldn't load today's list.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const doReplay = useCallback(async () => {
    const outcome = await replayQueue({
      complete_cadence: async (item) => {
        const p = item.payload as { cadenceId: string; completedBy: string; completedAt: string };
        const result = await postCompletion({
          cadence_id: p.cadenceId,
          completed_by: p.completedBy,
          idempotency_key: item.idempotencyKey,
          completed_at: p.completedAt,
        });
        completionIdRef.current.set(p.cadenceId, result.id);
      },
      complete_assignment: async (item) => {
        const p = item.payload as { assignmentId: string; completedAt: string };
        await completeAssignment(p.assignmentId, {
          idempotency_key: item.idempotencyKey,
          completed_at: p.completedAt,
        });
      },
      uncomplete_assignment: async (item) => {
        const p = item.payload as { assignmentId: string };
        await uncompleteAssignment(p.assignmentId, {
          idempotency_key: item.idempotencyKey,
        });
      },
      uncomplete_cadence: async (item) => {
        const p = item.payload as { completionId: string };
        await undoCadenceCompletion(p.completionId, item.idempotencyKey);
      },
    });

    for (const item of outcome.succeeded) {
      for (const [taskId, entry] of unsyncedRef.current.entries()) {
        if (entry.idempotencyKey === item.idempotencyKey) unsyncedRef.current.delete(taskId);
      }
    }
    if (outcome.requiresReauth) {
      // Session expired mid-queue-replay: the item(s) stay queued (never
      // dropped — see queue.ts), no refresh attempt (it would just 401
      // again too). Surfaced as a distinct, gentler banner than the
      // offline one; replay naturally resumes next time this hook mounts
      // fresh with a valid cookie (the helper re-opening their link).
      setNeedsReauth(true);
    } else if (outcome.succeeded.length > 0 || outcome.failed.length > 0) {
      setNeedsReauth(false);
      reportNetworkSuccess();
      await refresh();
    }
  }, [refresh, reportNetworkSuccess]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onOnline = () => {
      void doReplay();
    };
    window.addEventListener("online", onOnline);
    // Also attempt a replay on load, in case the queue survived a reload
    // while still offline-but-now-online.
    if (typeof navigator === "undefined" || navigator.onLine) void doReplay();
    return () => window.removeEventListener("online", onOnline);
  }, []);

  // Unmount: never leave a settle timer firing into a dead component, and
  // never leave a row stuck "settling" in the shared store.
  useEffect(() => {
    const timers = settleTimersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
      clearAllSettling();
    };
  }, []);

  /**
   * A mutation just failed and was enqueued (network error or 401) — this
   * decides how to reflect that. For a 401, always surface the reauth
   * banner. For a network error, the naive move is "show the offline
   * banner" — but that request may have been in flight since BEFORE the
   * device reconnected and only rejected just now (a real, observed race:
   * `fetch()` under Chromium's offline emulation can take noticeably
   * longer to reject than the `online` DOM event takes to fire — the
   * fetch was already doomed the moment the device went offline, but its
   * rejection can land milliseconds AFTER reconnecting). Blindly calling
   * `reportNetworkFailure()` in that case would re-show the offline
   * banner right after it correctly disappeared, with nothing left to
   * ever clear it (no further `online` event is coming — the browser
   * already considers itself online). So: only flip the banner on if
   * `navigator.onLine` is ALSO still false at this exact moment; if we're
   * actually back online already, skip the banner and kick off an
   * immediate replay instead — the item is safely queued either way.
   */
  const afterEnqueueFailure = useCallback(
    (err: unknown) => {
      if (isAuthError(err)) {
        setNeedsReauth(true);
        return;
      }
      const stillOffline = typeof navigator !== "undefined" && !navigator.onLine;
      if (stillOffline) {
        reportNetworkFailure();
      } else {
        void doReplay();
      }
    },
    [reportNetworkFailure, doReplay],
  );

  function showSnackbar(message: string, onUndo?: () => void, key: string | null = null) {
    if (snackbarTimerRef.current) clearTimeout(snackbarTimerRef.current);
    toastKeyRef.current = key;
    setToast({ message, onUndo });
    snackbarTimerRef.current = setTimeout(() => {
      toastKeyRef.current = null;
      setToast(null);
    }, SNACKBAR_MS);
  }

  /** Clears the regular toast only — a persistent failure alert stays up
   * until its own "Try again"/"Dismiss" clears it. */
  function dismissSnackbar() {
    if (snackbarTimerRef.current) clearTimeout(snackbarTimerRef.current);
    toastKeyRef.current = null;
    setToast(null);
  }

  /** Pull the toast if (and only if) it's about `key` — used when a save
   * permanently fails, so "Marked X done" never outlives the truth. */
  function withdrawToastFor(key: string) {
    if (toastKeyRef.current === key) dismissSnackbar();
  }

  function removeFailure(key: string) {
    setFailures((prev) => prev.filter((f) => f.key !== key));
  }

  function addFailure(key: string, title: string, retry: () => void) {
    const alert: SnackbarAlert = {
      key,
      message: saveFailedMessage(title),
      actionLabel: "Try again",
      onAction: () => {
        removeFailure(key);
        retry();
      },
      onDismiss: () => removeFailure(key),
    };
    setFailures((prev) => [...prev.filter((f) => f.key !== key), alert]);
  }

  /** Move a settled row from its open group into Done today. A no-op if
   * a refresh already moved it (or it's gone). */
  function moveToDone(taskId: string, group: GroupKey) {
    settleTimersRef.current.delete(taskId);
    setData((prev) => {
      if (!prev) return prev;
      const next: DueResponse = { ...prev };
      const removed = removeFromGroup(next, group, taskId);
      if (!removed) return prev;
      next.done_today = [{ ...removed }, ...next.done_today];
      return next;
    });
    clearSettling(taskId);
  }

  /** Cancel a pending settle (the row never left its group). Returns true
   * if there was one. */
  function cancelSettle(taskId: string): boolean {
    const timer = settleTimersRef.current.get(taskId);
    if (timer === undefined) return false;
    clearTimeout(timer);
    settleTimersRef.current.delete(taskId);
    clearSettling(taskId);
    return true;
  }

  const completeTask = useCallback(
    (task: DueTask, group: GroupKey, retryOf?: Attempt) => {
      // The row stays where it is, checked and struck through, for
      // `SETTLE_MS` — then moves to Done today. The SAVE below is sent
      // right now regardless; only the visual move waits.
      originRef.current.set(task.id, group);
      cancelSettle(task.id);
      markSettling(task.id);
      settleTimersRef.current.set(
        task.id,
        setTimeout(() => moveToDone(task.id, group), SETTLE_MS),
      );

      const idempotencyKey = retryOf?.idempotencyKey ?? crypto.randomUUID();
      const completedAt = retryOf?.completedAt ?? nowIso();
      attemptRef.current.set(task.id, idempotencyKey);
      // A fresh tap supersedes any earlier "Couldn't save" alert for this
      // row — its stale Try again would re-send under the OLD key and
      // double-record the chore.
      removeFailure(task.id);

      const pendingUndo = undoInflightRef.current.get(task.id);
      const send = async (): Promise<SendOutcome> => {
        if (pendingUndo) await pendingUndo;
        try {
          if (task.kind === "cadence") {
            const result = await postCompletion({
              cadence_id: task.id,
              completed_by: completedByFor(task.assignee_id, meId),
              idempotency_key: idempotencyKey,
              completed_at: completedAt,
            });
            completionIdRef.current.set(task.id, result.id);
          } else {
            await completeAssignment(task.id, {
              idempotency_key: idempotencyKey,
              completed_at: completedAt,
            });
          }
          return "ok";
        } catch (err) {
          if (isQueueable(err)) {
            const kind: QueueMutationKind =
              task.kind === "cadence" ? "complete_cadence" : "complete_assignment";
            await enqueueMutation(
              kind,
              idempotencyKey,
              task.kind === "cadence"
                ? {
                    cadenceId: task.id,
                    completedBy: completedByFor(task.assignee_id, meId),
                    completedAt,
                  }
                : { assignmentId: task.id, completedAt },
            );
            unsyncedRef.current.set(task.id, { kind, idempotencyKey });
            afterEnqueueFailure(err);
            return "queued";
          }
          // Permanent failure (e.g. 404/409/422) — roll the optimistic UI
          // back AND say so, persistently. Skipped if the person already
          // un-did this task (nothing on screen claims it's done anymore).
          console.warn(`Couldn't mark "${task.title}" done:`, err);
          if (attemptRef.current.get(task.id) !== idempotencyKey) return "failed";
          attemptRef.current.delete(task.id);
          if (!cancelSettle(task.id)) {
            setData((prev) => {
              if (!prev) return prev;
              const next: DueResponse = { ...prev };
              const removed = removeFromGroup(next, "done_today", task.id);
              if (removed) next[group] = [removed, ...next[group]];
              return next;
            });
          }
          originRef.current.delete(task.id);
          withdrawToastFor(task.id);
          addFailure(task.id, task.title, () =>
            completeTask(task, group, { idempotencyKey, completedAt }),
          );
          return "failed";
        }
      };
      const pending = send();
      inflightRef.current.set(task.id, pending);
      void pending.finally(() => {
        if (inflightRef.current.get(task.id) === pending) inflightRef.current.delete(task.id);
      });

      showSnackbar(`Marked "${task.title}" done`, () => undoTask(task), task.id);
    },
    [afterEnqueueFailure, meId],
  );

  const undoTask = useCallback(
    (task: DueTask) => {
      const origin = originRef.current.get(task.id) ?? "due_today";
      // Still inside its settle window? The row never left `origin` —
      // just stop the pending move. (Also try the Done-today move below,
      // in case a refresh landed it there in the meantime.)
      cancelSettle(task.id);
      setData((prev) => {
        if (!prev) return prev;
        const next: DueResponse = { ...prev };
        const removed = removeFromGroup(next, "done_today", task.id);
        if (!removed) return prev;
        next[origin] = [removed, ...next[origin]];
        return next;
      });
      originRef.current.delete(task.id);
      attemptRef.current.delete(task.id);
      removeFailure(task.id);
      dismissSnackbar();

      const inflight = inflightRef.current.get(task.id);
      const idempotencyKey = crypto.randomUUID();
      const undo = async () => {
        // An Undo tapped while the completion POST is still in flight
        // waits for it, so it knows whether there's a queued copy to
        // cancel, a real completion to reverse, or nothing at all.
        if (inflight && (await inflight) === "failed") return;

        const stillQueued = unsyncedRef.current.get(task.id);
        if (stillQueued) {
          // Never actually left this device — cancel it outright, no
          // network round trip and no server-side reversal needed at all.
          void cancelQueuedMutation(stillQueued.kind, stillQueued.idempotencyKey);
          unsyncedRef.current.delete(task.id);
          return;
        }

        try {
          if (task.kind === "assignment") {
            await uncompleteAssignment(task.id, { idempotency_key: idempotencyKey });
          } else {
            const completionId = completionIdRef.current.get(task.id);
            if (!completionId) throw new ApiError("no completion id on file", 0, false);
            await undoCadenceCompletion(completionId, idempotencyKey);
          }
        } catch (err) {
          if (isQueueable(err)) {
            const kind: QueueMutationKind =
              task.kind === "assignment" ? "uncomplete_assignment" : "uncomplete_cadence";
            await enqueueMutation(
              kind,
              idempotencyKey,
              task.kind === "assignment"
                ? { assignmentId: task.id }
                : { completionId: completionIdRef.current.get(task.id) ?? "" },
            );
            afterEnqueueFailure(err);
          } else {
            // Permanent failure (e.g. a stale/unknown completion id) —
            // cannot silently leave the UI showing "open" while the server
            // still has it recorded done. Put it back to done and say so.
            console.warn(`Couldn't undo "${task.title}":`, err);
            setData((prev) => {
              if (!prev) return prev;
              const next: DueResponse = { ...prev };
              const removed = removeFromGroup(next, origin, task.id);
              if (removed) next.done_today = [removed, ...next.done_today];
              return next;
            });
            originRef.current.set(task.id, origin);
            showSnackbar(`Couldn't undo "${task.title}"`);
          }
        }
      };
      const pendingUndo = undo();
      undoInflightRef.current.set(task.id, pendingUndo);
      void pendingUndo
        .finally(() => {
          if (undoInflightRef.current.get(task.id) === pendingUndo) {
            undoInflightRef.current.delete(task.id);
          }
        })
        .catch(() => {});
    },
    [afterEnqueueFailure],
  );

  const toggleTask = useCallback(
    (task: DueTask, group: GroupKey) => {
      // A row inside its settle window is still rendered in its open
      // group, but it IS done — a second tap there is an undo.
      if (group === "done_today" || settleTimersRef.current.has(task.id)) undoTask(task);
      else completeTask(task, group);
    },
    [completeTask, undoTask],
  );

  /**
   * Logs one occurrence toward a `flexible` cadence's weekly target (My
   * list's "Aim for: N times this week (done M)" rows). Unlike a regular
   * due/overdue task, a flexible chore never leaves its list and has no
   * single-item "done" state — only the server-owned `done_count`/phrase
   * changes, so we never mutate that phrase locally (per the copy-
   * dictionary rule: the server renders every cadence phrase). The local
   * `done_count` IS nudged optimistically (the Dashboard's progress bar
   * reads it) and rolled back on a permanent failure, same as a task row.
   * A successful sync triggers `refresh()` so the new phrase comes from
   * the server; offline, the tap is queued exactly like any other
   * completion.
   */
  const logFlexibleTask = useCallback(
    (
      item: { cadence_id: string; title: string; assignee_id: string | null },
      retryOf?: Attempt,
    ) => {
      const idempotencyKey = retryOf?.idempotencyKey ?? crypto.randomUUID();
      const completedAt = retryOf?.completedAt ?? nowIso();
      // One key per logged occurrence: each tap is its own completion, so
      // an Undo always reverses the tap its own toast was about.
      const taskKey = `flexible:${idempotencyKey}`;
      let undone = false;

      setData((prev) => (prev ? bumpFlexible(prev, item.cadence_id, 1) : prev));

      const send = async (): Promise<SendOutcome> => {
        try {
          const result = await postCompletion({
            cadence_id: item.cadence_id,
            completed_by: completedByFor(item.assignee_id, meId),
            idempotency_key: idempotencyKey,
            completed_at: completedAt,
          });
          completionIdRef.current.set(taskKey, result.id);
          await refresh();
          return "ok";
        } catch (err) {
          if (isQueueable(err)) {
            await enqueueMutation("complete_cadence", idempotencyKey, {
              cadenceId: item.cadence_id,
              completedBy: completedByFor(item.assignee_id, meId),
              completedAt,
            });
            unsyncedRef.current.set(taskKey, { kind: "complete_cadence", idempotencyKey });
            afterEnqueueFailure(err);
            return "queued";
          }
          // Permanent failure — roll back the optimistic count, pull the
          // now-untrue toast, and say so persistently (same as tasks).
          console.warn(`Couldn't log "${item.title}":`, err);
          if (undone) return "failed";
          setData((prev) => (prev ? bumpFlexible(prev, item.cadence_id, -1) : prev));
          withdrawToastFor(taskKey);
          addFailure(taskKey, item.title, () =>
            logFlexibleTask(item, { idempotencyKey, completedAt }),
          );
          return "failed";
        }
      };
      const pending = send();

      showSnackbar(
        `Marked "${item.title}" done`,
        () => {
          undone = true;
          setData((prev) => (prev ? bumpFlexible(prev, item.cadence_id, -1) : prev));
          void (async () => {
            if ((await pending) === "failed") return;
            const stillQueued = unsyncedRef.current.get(taskKey);
            if (stillQueued) {
              void cancelQueuedMutation(stillQueued.kind, stillQueued.idempotencyKey);
              unsyncedRef.current.delete(taskKey);
              return;
            }
            const completionId = completionIdRef.current.get(taskKey);
            if (!completionId) return;
            const undoKey = crypto.randomUUID();
            await undoCadenceCompletion(completionId, undoKey)
              .then(() => refresh())
              .catch((err: unknown) => {
                if (isQueueable(err)) {
                  void enqueueMutation("uncomplete_cadence", undoKey, { completionId });
                  afterEnqueueFailure(err);
                  return;
                }
                console.warn(`Couldn't undo "${item.title}":`, err);
                setData((prev) => (prev ? bumpFlexible(prev, item.cadence_id, 1) : prev));
                showSnackbar(`Couldn't undo "${item.title}"`);
              });
          })();
        },
        taskKey,
      );
    },
    [refresh, afterEnqueueFailure, meId],
  );

  // One `SnackbarState` for the one `<Snackbar>` `app.tsx` mounts: the
  // regular toast (if any) plus every outstanding failure alert. Memoized
  // so `Snackbar`'s focus-on-Undo effect doesn't re-fire on every render.
  const snackbar = useMemo<SnackbarState | null>(() => {
    if (!toast && failures.length === 0) return null;
    if (failures.length === 0) return toast;
    return { ...(toast ?? { message: "" }), alerts: failures };
  }, [toast, failures]);

  return {
    data,
    loading,
    error,
    isOffline,
    needsReauth,
    snackbar,
    dismissSnackbar,
    toggleTask,
    logFlexibleTask,
    refresh,
  };
}
